import { Flex, Select } from 'core/components';
import VariableTag from 'app/views/boards/VariableTag';
import { InputGroup } from 'core/form';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Classes } from '@blueprintjs/core';
import classNames from 'classnames';
import { useGuidedDimensionOptions } from './guidedDimensionLookups';

const FILTER_DEBOUNCE_MS = 400;

const VariableWrapper = styled.div`
  display: flex;
  padding: 0;
  /* Hug content size; never exceed the row's available width */
  flex: 0 1 auto;
  /* Override flex item default min-width: auto so flex-shrink can take effect */
  min-width: 0;
  max-width: 100%;

  .variable-label {
    display: flex;
    align-items: center;
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    font-size: 14px;
    padding: 0 12px;
    height: 30px;
    flex-shrink: 0;
    text-align: left;
    position: relative;
    right: -1px; /* visually connect the label to the control by overlapping borders */
  }

  .${Classes.INPUT} {
    padding-left: 8px;
    font-size: 13px;
    line-height: 20px;
    height: 30px;
    min-height: 30px;
  }

  > div:last-child .${Classes.BUTTON}, .${Classes.INPUT} {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  /* Inner Select wrapper: hug content but allow shrinking with ellipsis */
  > div:last-child {
    flex: 0 1 auto;
    min-width: 0;
    max-width: 100%;
  }

  /* The Blueprint Popover renders <span class="bp4-popover-wrapper"> which is
     inline-level by default. That breaks the width cascade so the inner input
     never realizes it must shrink. Force them to participate as block / flex. */
  > div:last-child > div,
  .${Classes.POPOVER_WRAPPER}, .${Classes.POPOVER_TARGET} {
    display: flex;
    max-width: 100%;
    min-width: 0;
    width: 100%;
  }

  /* Allow tags to shrink and the inner text to ellipsis. Blueprint sets
     min-width: auto on flex children which prevents shrinking past content size,
     so we must explicitly set min-width: 0 on every shrinking link in the chain. */
  .${Classes.TAG} {
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
  }
  .${Classes.TAG} > .${Classes.FILL} {
    min-width: 0;
    overflow: hidden;
  }

  &.disabled {
    opacity: 0.4;
  }
`;

function BoardVariable({
  index,
  enabled = true,
  name,
  field,
  type,
  value,
  allowMulti,
  exactMatch,
  options,
  operator = '=',
  onFilterChange,
  onOperatorChange,
  onHover,
  onFocus
}) {
  const [filterValue, setFilterValue] = useState(allowMulti ? (value ?? []) : (value ?? ''));
  const [filterOperator, setFilterOperator] = useState(operator);

  // Sync external value changes (e.g. populated from URL params after mount) into
  // local state.  We use JSON.stringify as the dependency key so that array values
  // with identical content don't trigger unnecessary updates.
  useEffect(() => {
    setFilterValue(allowMulti ? (value ?? []) : (value ?? ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value), allowMulti]);

  useEffect(() => {
    setFilterOperator(operator);
  }, [operator]);

  const guidedOptions = useGuidedDimensionOptions(type === 'guided' ? field || name : null);

  const guidedQueryFn = type === 'guided' && typeof guidedOptions === 'function' ? guidedOptions : null;

  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;

  const debouncedOnFilterChange = useMemo(
    () => debounce((idx, val) => onFilterChangeRef.current(idx, val), FILTER_DEBOUNCE_MS),
    []
  );

  useEffect(() => () => debouncedOnFilterChange.cancel(), [debouncedOnFilterChange]);

  const handleInputChange = useCallback(
    (event) => {
      const newVal = event.target.value;
      setFilterValue(newVal);
      debouncedOnFilterChange(index, newVal);
    },
    [debouncedOnFilterChange, index]
  );

  const handleSelectChange = useCallback(
    (newVal) => {
      setFilterValue(newVal);
      debouncedOnFilterChange(index, newVal);
    },
    [debouncedOnFilterChange, index]
  );

  const handleOperatorChange = useCallback(
    (newOp) => {
      setFilterOperator(newOp);
      if (onOperatorChange) {
        onOperatorChange(index, newOp);
      }
    },
    [index, onOperatorChange]
  );

  const selectOptions = useMemo(
    () => {
      if (guidedQueryFn) {
        return []; // options come dynamically from onQuery
      }
      const source = type === 'guided' ? guidedOptions : options;
      return Array.isArray(source)
        ? source
            .filter((opt) => opt.value || opt.label)
            .map((opt) => ({
              value: opt.value,
              label: opt.label || opt.value,
              id: opt.id,
              name: opt.name,
              matcher: opt.matcher
            }))
        : [];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [type, guidedOptions, guidedQueryFn, options]
  );

  // For guided types without a lookup (e.g. IPAddress), guidedQueryFn is null and
  // selectOptions is empty — in that case fall through to the plain text InputGroup.
  const hasOptions = guidedQueryFn !== null || selectOptions.length > 0;

  let selectValues = filterValue;
  if (allowMulti) {
    selectValues = Array.isArray(filterValue) ? filterValue : [];
  }

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const selectRef = useRef(null);

  const handleLabelClick = useCallback(() => {
    if (!hasOptions) {
      inputRef.current?.focus();
      return;
    }
    // Defer past the current click's bubble cycle: showOptions() attaches a
    // document click listener, and if we call it synchronously the same click
    // event bubbles to document and immediately triggers closeOptions().
    setTimeout(() => selectRef.current?.showOptions(), 0);
  }, [hasOptions]);

  const showOperatorDropdown = type === 'guided' && !hasOptions;
  const operatorOptions = [
    { value: '=', label: 'equals' },
    { value: '<>', label: 'does not equal' },
    { value: 'ILIKE', label: 'contains' },
    { value: 'NOT ILIKE', label: 'does not contain' },
    { value: '~*', label: 'matches regex' }
  ];

  const control = hasOptions ? (
    <Select
      disabled={!enabled}
      options={selectOptions}
      onQuery={guidedQueryFn}
      values={selectValues}
      multi={allowMulti}
      exactMatch={exactMatch}
      showFilter
      onChange={handleSelectChange}
      placeholder="All"
      responsive
      componentRef={selectRef}
    />
  ) : (
    <Flex flex="1 1 auto" minWidth={0}>
      {showOperatorDropdown ? (
        <Select
          fill
          disabled={!enabled}
          options={operatorOptions}
          values={[filterOperator]}
          onChange={(selected) => handleOperatorChange(Array.isArray(selected) ? selected[0] : selected || '=')}
          placeholder="equals"
          menuWidth={150}
          style={{ minWidth: 150, maxWidth: 150 }}
        />
      ) : null}
      <InputGroup disabled={!enabled} value={filterValue} onChange={handleInputChange} inputRef={inputRef} />
    </Flex>
  );

  return (
    <VariableWrapper
      ref={wrapperRef}
      className={classNames({ disabled: !enabled })}
      onMouseEnter={() => onHover(name)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onFocus(name)}
      onBlur={() => onFocus(null)}
    >
      <button
        type="button"
        className={classNames(Classes.BUTTON, 'variable-label', { 'variable-label--custom': type === 'custom' })}
        onClick={handleLabelClick}
      >
        <VariableTag name={name} asTag={false} icon={type === 'custom' ? 'variable' : null} />
      </button>
      {control}
    </VariableWrapper>
  );
}

function BoardVariables({ board, onPresetFiltersChange, onVariableHover }) {
  const [hoveredVariable, setHoveredVariable] = useState(null);
  const [focusedVariable, setFocusedVariable] = useState(null);

  useEffect(() => {
    onVariableHover(focusedVariable || hoveredVariable);
  }, [hoveredVariable, focusedVariable, onVariableHover]);

  const boardVariables = board.get('config.variables') || [];

  const handleFilterChange = (index, value) => {
    const updatedFilters = [...boardVariables];
    updatedFilters.splice(index, 1, { ...updatedFilters[index], value });
    onPresetFiltersChange(updatedFilters);
  };

  const handleOperatorChange = (index, operator) => {
    const updatedFilters = [...boardVariables];
    updatedFilters.splice(index, 1, { ...updatedFilters[index], operator });
    onPresetFiltersChange(updatedFilters);
  };

  return (
    <Flex display="contents">
      {boardVariables.map((boardVariable, index) => (
        <BoardVariable
          key={boardVariable.name}
          onFilterChange={handleFilterChange}
          onOperatorChange={handleOperatorChange}
          onHover={setHoveredVariable}
          onFocus={setFocusedVariable}
          board={board}
          index={index}
          {...boardVariable}
        />
      ))}
    </Flex>
  );
}

export default BoardVariables;
